import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  get,
  italic,
  type LegalValues,
  legalOrdinal,
  mainTitle,
  mlaUrl,
  plain,
  titleCase,
  wholeNumber,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * Published congressional hearings and testimony in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 §11.6 (Publication Manual p. 364), as reproduced by Liberty University, Wake
 *   Forest and Widener: full hearing — "Title of hearing, xxx Cong. (Year). URL",
 *   "Strengthening the federal student loan program for borrowers: Hearing before the
 *   U.S. Senate Committee on Health, Education, Labor & Pensions, 113th Cong. (2014)."
 *   Testimony adds "(testimony of Testifier Name)". The title is italic, in sentence
 *   case; in text: (Title, Year), with the title in title case.
 * - Bluebook 22d ed. rule 13.3 (Tarlton Law Library; LLSDC): the title as on the cover,
 *   "Hearing Before the Subcomm. on … of the H. Comm. on …", the Congress, the page
 *   cited, the year the hearing record was published, and "(statement of …)" —
 *   "Federal Reserve Accountability and Reform: Hearing Before the S. Comm. on Banking,
 *   Housing, and Urban Affairs, 114th Cong. (2015)." Other words in committee names
 *   may also be shortened with tables T6 and T9; only Comm. and Subcomm. are here.
 * - Chicago 18 §14.185 follows that form: "Homeland Security Act of 2002: Hearings on
 *   H.R. 5005, Day 3, Before the Select Comm. on Homeland Security, 107th Cong. 203
 *   (2002) (statement of David Walker, Comptroller General of the United States)."
 *   (Lone Star College CMOS 18 guide.) Notes only.
 * - MLA 9: MLA Style Center, "Documenting Legal Works" — "United States, Congress,
 *   House, Committee on Education and Labor. The Future of Learning: How Technology Is
 *   Transforming Public Schools. U.S. Government Publishing Office, 16 June 2009,
 *   www.gpo.gov/…" — the committee as author, the hearing title in italics.
 * - Hearing facts for the page example: GovInfo, S. Hrg. 113-829 (CHRG-113shrg22607),
 *   held March 27, 2014, printed 2016; Deanne Loonin's statement begins on page 40.
 */

export const HEARING_FIELDS = {
  title: "title",
  shortTitle: "shortTitle",
  chamber: "chamber",
  committee: "committee",
  subcommittee: "subcommittee",
  congress: "congress",
  date: "date",
  printedYear: "printedYear",
  witness: "witness",
  witnessRole: "witnessRole",
  page: "page",
  publisher: "publisher",
  url: "url",
} as const;

const F = HEARING_FIELDS;

export type HearingChamber = "senate" | "house" | "joint";

export interface HearingLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

const CHAMBERS: Readonly<
  Record<HearingChamber, { apa: string; mla: string; legal: string }>
> = {
  senate: { apa: "U.S. Senate ", mla: "Senate", legal: "S. " },
  house: { apa: "U.S. House ", mla: "House", legal: "H. " },
  joint: { apa: "", mla: "", legal: "" },
};

/** A title that already says "Hearing before …" is used as typed. */
const RE_HEARING_WORDS = /\bhearings?\s+(before|on|of)\b/i;
const RE_SUBCOMMITTEE = /\bSubcommittee\b/g;
const RE_COMMITTEE = /\bCommittee\b/g;

/** Bluebook's committee abbreviations: "Comm." and "Subcomm." */
function abbreviateCommittee(name: string): string {
  return name
    .replace(RE_SUBCOMMITTEE, "Subcomm.")
    .replace(RE_COMMITTEE, "Comm.");
}

interface Hearing {
  title: string;
  short: string;
  chamber: (typeof CHAMBERS)[HearingChamber];
  committee: string;
  subcommittee: string;
  congress: string;
  year: string;
  printedYear: string;
  date: ReturnType<typeof parsePartialDate>;
  witness: string;
  witnessRole: string;
  page: string;
  publisher: string;
  url: string;
}

function readHearing(values: LegalValues): Hearing {
  const title = get(values, F.title);
  const date = parsePartialDate(get(values, F.date));
  const congress = wholeNumber(get(values, F.congress));
  const chamberKey = (get(values, F.chamber) || "senate") as HearingChamber;
  return {
    title,
    short: titleCase(get(values, F.shortTitle) || mainTitle(title)),
    chamber: CHAMBERS[chamberKey] ?? CHAMBERS.senate,
    committee: get(values, F.committee),
    subcommittee: get(values, F.subcommittee),
    congress: congress ? `${legalOrdinal(congress)} Cong.` : "",
    year: date ? String(date.year) : "n.d.",
    printedYear: get(values, F.printedYear),
    date,
    witness: get(values, F.witness),
    witnessRole: get(values, F.witnessRole),
    page: get(values, F.page),
    publisher: get(values, F.publisher),
    url: get(values, F.url),
  };
}

/** APA: "…: Hearing before the Subcommittee on X of the U.S. Senate Committee on Y". */
function apaHearingTitle(hearing: Hearing): string {
  if (RE_HEARING_WORDS.test(hearing.title) || hearing.committee === "") {
    return hearing.title;
  }
  const committee = `${hearing.chamber.apa}${hearing.committee}`;
  const before = hearing.subcommittee
    ? `${hearing.subcommittee} of the ${committee}`
    : committee;
  return `${hearing.title}: Hearing before the ${before}`;
}

/** Bluebook and Chicago: "…: Hearing Before the Subcomm. on X of the H. Comm. on Y". */
function legalHearingTitle(hearing: Hearing): string {
  const title = titleCase(hearing.title);
  if (RE_HEARING_WORDS.test(hearing.title) || hearing.committee === "") {
    return title;
  }
  const committee = `${hearing.chamber.legal}${abbreviateCommittee(hearing.committee)}`;
  const before = hearing.subcommittee
    ? `${abbreviateCommittee(hearing.subcommittee)} of the ${committee}`
    : committee;
  return `${title}: Hearing Before the ${before}`;
}

function apaOutput(hearing: Hearing, labels: HearingLabels): CitationOutput {
  const testimony = hearing.witness ? ` (testimony of ${hearing.witness})` : "";
  const congress = hearing.congress ? `, ${hearing.congress}` : "";
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      {
        label: labels.referenceList,
        parts: [
          italic(apaHearingTitle(hearing)),
          plain(
            `${congress} (${hearing.year})${testimony}.${
              hearing.url ? ` ${hearing.url}` : ""
            }`
          ),
        ],
      },
      {
        label: labels.parenthetical,
        parts: [plain("("), italic(hearing.short), plain(`, ${hearing.year})`)],
      },
      {
        label: labels.narrative,
        parts: [italic(hearing.short), plain(` (${hearing.year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

function mlaOutput(hearing: Hearing, labels: HearingLabels): CitationOutput {
  const author = [
    "United States",
    "Congress",
    hearing.chamber.mla,
    hearing.committee,
    hearing.subcommittee,
  ]
    .filter(Boolean)
    .join(", ");
  const title = titleCase(hearing.title);
  const details = [
    hearing.publisher,
    mlaDate(hearing.date),
    mlaUrl(hearing.url),
  ].filter(Boolean);
  const parts: CitationParts = [
    plain(`${author}. `),
    italic(title),
    plain(closer(title)),
    plain(details.length > 0 ? ` ${details.join(", ")}.` : ""),
  ];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      {
        label: labels.inText,
        parts: [plain("(United States, "), italic(hearing.short), plain(")")],
      },
    ],
    note: labels.mlaNote,
  };
}

function chicagoOutput(
  hearing: Hearing,
  labels: HearingLabels
): CitationOutput {
  const page = hearing.page ? ` ${hearing.page}` : "";
  const year = hearing.printedYear || hearing.year;
  const statement = hearing.witness
    ? ` (statement of ${[hearing.witness, hearing.witnessRole]
        .filter(Boolean)
        .join(", ")})`
    : "";
  const congress = hearing.congress ? `, ${hearing.congress}${page}` : "";
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      {
        label: labels.footnote,
        parts: [
          italic(legalHearingTitle(hearing)),
          plain(
            `${congress} (${year})${statement}${
              hearing.url ? `, ${hearing.url}` : ""
            }.`
          ),
        ],
      },
    ],
    note: labels.chicagoNote,
  };
}

export function hearingIsReady(values: LegalValues): boolean {
  return (
    get(values, F.title) !== "" &&
    get(values, F.congress) !== "" &&
    parsePartialDate(get(values, F.date)) !== null
  );
}

export function formatHearing(
  values: LegalValues,
  labels: HearingLabels
): CitationOutput[] {
  const hearing = readHearing(values);
  return [
    apaOutput(hearing, labels),
    mlaOutput(hearing, labels),
    chicagoOutput(hearing, labels),
  ];
}
